import React, { useEffect, useMemo } from "react";
import {
  Box,
  Chip,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { InsightsAssistant } from "./InsightsAssistant";
import { useInsightsChatbotConfig } from "../context";
import type {
  ChatbotQueryRequest,
  StandardInsightsResponse,
} from "../api";

type Insights = StandardInsightsResponse["data"];

interface AiWellnessPanelProps {
  insights: Insights;
  scopeLabel: string;
  buildRequest: (question: string) => ChatbotQueryRequest;
  initialPrompt: string;
  /**
   * Unique key for the current scope (e.g. "team:abc", "user:xyz", "org").
   * When it changes the conversation is cleared, so insights from one team/
   * user/org don't bleed into another.
   */
  scopeKey: string;
}

/** Animated conic-gradient wellness score ring. */
const ScoreRing: React.FC<{ value: number; color: string }> = ({
  value,
  color,
}) => {
  const theme = useTheme();
  const pct = Math.max(0, Math.min(100, value));
  return (
    <Box
      sx={{
        position: "relative",
        width: 148,
        height: 148,
        borderRadius: "50%",
        flexShrink: 0,
        background: `conic-gradient(${color} ${pct * 3.6}deg, ${alpha(
          theme.palette.common.white,
          0.15
        )} 0deg)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.8s ease",
      }}
    >
      <Box
        sx={{
          width: 118,
          height: 118,
          borderRadius: "50%",
          bgcolor: theme.palette.background.paper,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h3" fontWeight={800} lineHeight={1}>
          {Math.round(value)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          / 100
        </Typography>
      </Box>
    </Box>
  );
};

// rag_color may arrive as a CSS-invalid named token (e.g. "amber"/"green").
// Map known names to theme colours; pass through valid CSS, else fall back.
const resolveColor = (raw: string | undefined, fallback: string, theme: Theme): string => {
  if (!raw) return fallback;
  const named: Record<string, string> = {
    green: theme.palette.success.main,
    amber: theme.palette.warning.main,
    orange: theme.palette.warning.main,
    yellow: theme.palette.warning.main,
    red: theme.palette.error.main,
    grey: theme.palette.text.secondary,
    gray: theme.palette.text.secondary,
  };
  const key = raw.trim().toLowerCase();
  if (named[key]) return named[key];
  if (/^(#|rgb|rgba|hsl|hsla|color\()/.test(key)) return raw;
  return fallback;
};

const scoreColor = (value: number, theme: Theme) =>
  value >= 75
    ? theme.palette.success.main
    : value >= 50
      ? theme.palette.warning.main
      : theme.palette.error.main;

export const AiWellnessPanel: React.FC<AiWellnessPanelProps> = ({
  insights,
  scopeLabel,
  buildRequest,
  initialPrompt,
  scopeKey,
}) => {
  const theme = useTheme();
  const { setDraft, resetChat } = useInsightsChatbotConfig();

  // Clear the conversation whenever the scope changes (team → user → org, or a
  // different team) so one scope's chat never carries into another. The chat is
  // persisted globally, so we compare against the last scope in sessionStorage —
  // this also catches navigating between pages (each remounts the panel).
  useEffect(() => {
    const STORE_KEY = "peak4.insightsChatbot.scope";
    const last =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem(STORE_KEY)
        : null;
    if (last !== scopeKey) {
      resetChat();
      try {
        window.sessionStorage.setItem(STORE_KEY, scopeKey);
      } catch {
        // ignore storage errors
      }
    }
  }, [scopeKey, resetChat]);

  const score = insights.overall_wellness_score.value;
  const accent = scoreColor(score, theme);

  const components = useMemo(
    () => Object.values(insights.overall_wellness_score.components ?? {}),
    [insights.overall_wellness_score.components]
  );

  // Clicking any insight seeds a targeted question into the assistant so the
  // admin can drill in without retyping. We scroll the chat into view.
  const ask = (question: string) => {
    setDraft(question);
    document
      .getElementById("ai-wellness-assistant")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <Stack spacing={3}>
      {/* ── Hero score ─────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 4,
          p: { xs: 3, md: 4 },
          color: "#fff",
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(
            accent,
            0.85
          )} 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.12,
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #fff 0, transparent 40%), radial-gradient(circle at 80% 60%, #fff 0, transparent 35%)",
          }}
        />
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="center"
          sx={{ position: "relative" }}
        >
          <ScoreRing value={score} color={accent} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <AutoAwesomeIcon fontSize="small" />
              <Typography variant="overline" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
                AI Wellness Score · {scopeLabel}
              </Typography>
            </Stack>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 1.5 }}>
              {score >= 75
                ? "Strong overall wellbeing"
                : score >= 50
                  ? "Mixed signals — worth a closer look"
                  : "Needs attention"}
            </Typography>

            {/* Component breakdown bars */}
            <Stack spacing={1} sx={{ maxWidth: 520 }}>
              {components.slice(0, 5).map((c) => (
                <Box
                  key={c.label}
                  onClick={() =>
                    ask(`Why is the "${c.label}" score ${Math.round(c.score)} and how can we improve it?`)
                  }
                  sx={{ cursor: "pointer", "&:hover .barLabel": { textDecoration: "underline" } }}
                >
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" className="barLabel" sx={{ fontWeight: 600 }}>
                      {c.label}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {Math.round(c.score)}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha("#fff", 0.2),
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(0, Math.min(100, c.score))}%`,
                        height: "100%",
                        borderRadius: 3,
                        bgcolor: "#fff",
                        transition: "width 0.8s ease",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* ── Interactive insight grid ───────────────────────────────── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <InsightColumn
            icon={<LightbulbIcon fontSize="small" />}
            title="Key highlights"
            color={theme.palette.success.main}
            empty="No highlights available."
            items={insights.key_highlights.map((h) => ({
              key: h.metric_id,
              primary: h.title,
              secondary: h.insight ?? `${h.value}${h.unit ? ` ${h.unit}` : ""}`,
              chip: h.rag_label,
              chipColor: h.rag_color,
              onClick: () =>
                ask(`Tell me more about "${h.title}" — what's driving it and is it a concern?`),
            }))}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <InsightColumn
            icon={<TrendingUpIcon fontSize="small" />}
            title="Recommended actions"
            color={theme.palette.primary.main}
            empty="No recommendations available."
            items={insights.personalised_recommendations.map((r, i) => ({
              key: `${r.title}-${i}`,
              primary: r.title,
              secondary: r.rationale,
              onClick: () =>
                ask(`Give me a step-by-step plan for: "${r.title}". What actions should we take?`),
            }))}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <InsightColumn
            icon={<WarningAmberIcon fontSize="small" />}
            title="Areas to watch"
            color={theme.palette.warning.main}
            empty="No watch areas."
            items={insights.areas_to_watch.map((a) => ({
              key: a.metric_id,
              primary: a.label,
              secondary: a.reason,
              chip: a.rag_label,
              chipColor: a.rag_color,
              onClick: () =>
                ask(`Why is "${a.label}" flagged as an area to watch, and what should we do about it?`),
            }))}
          />
        </Grid>
      </Grid>

      {/* ── Conversational assistant ───────────────────────────────── */}
      <Box id="ai-wellness-assistant">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Typography variant="h6" fontWeight={800}>
            Ask Peakie AI
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Interrogate this data — or tap any insight above to dig in.
          </Typography>
        </Stack>
        <InsightsAssistant buildRequest={buildRequest} initialPrompt={initialPrompt} />
      </Box>
    </Stack>
  );
};

interface InsightItem {
  key: string;
  primary: string;
  secondary?: string;
  chip?: string;
  chipColor?: string;
  onClick: () => void;
}

const InsightColumn: React.FC<{
  icon: React.ReactNode;
  title: string;
  color: string;
  items: InsightItem[];
  empty: string;
}> = ({ icon, title, color, items, empty }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height: "100%",
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, py: 1.5, bgcolor: alpha(color, 0.1), color }}
      >
        {icon}
        <Typography variant="subtitle2" fontWeight={800}>
          {title}
        </Typography>
        <Chip
          label={items.length}
          size="small"
          sx={{ ml: "auto", height: 20, bgcolor: alpha(color, 0.2), color, fontWeight: 700 }}
        />
      </Stack>

      <Stack sx={{ p: 1 }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
            {empty}
          </Typography>
        ) : (
          items.map((item) => (
            <Stack
              key={item.key}
              direction="row"
              spacing={1}
              alignItems="flex-start"
              onClick={item.onClick}
              sx={{
                p: 1.25,
                borderRadius: 2,
                cursor: "pointer",
                transition: "background 0.15s ease",
                "&:hover": { bgcolor: alpha(color, 0.08) },
                "&:hover .chev": { opacity: 1, transform: "translateX(0)" },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {item.primary}
                  </Typography>
                  {item.chip && (
                    <Chip
                      label={item.chip}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: 10,
                        bgcolor: alpha(resolveColor(item.chipColor, color, theme), 0.15),
                        color: resolveColor(item.chipColor, color, theme),
                      }}
                    />
                  )}
                </Stack>
                {item.secondary && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {item.secondary}
                  </Typography>
                )}
              </Box>
              <ChevronRightIcon
                className="chev"
                fontSize="small"
                sx={{
                  color,
                  opacity: 0,
                  transform: "translateX(-4px)",
                  transition: "all 0.15s ease",
                  mt: 0.25,
                }}
              />
            </Stack>
          ))
        )}
      </Stack>
    </Box>
  );
};
